/**
 * Monitor de Lazy Loading com Logging Detalhado
 *
 * Instrumentação para garantir que lazy loading não afeta:
 * - Service Worker cache
 * - syncWorker sincronização
 * - WatermelonDB operações
 *
 * USO:
 * import { monitorLazyLoad } from './utils/lazyLoadMonitor';
 * const Home = monitorLazyLoad(() => import('./pages/Home'), 'Home');
 */

const LOG_PREFIX = '[LAZY-LOAD-MONITOR]';
const LOG_ENABLED = process.env.NODE_ENV !== 'production';

// Estatísticas globais
const stats = {
  totalLoads: 0,
  successfulLoads: 0,
  failedLoads: 0,
  retries: 0,
  loadTimes: [],
  failedChunks: []
};

/**
 * Logger com timestamp e cores
 */
const log = {
  info: (...args) => {
    if (LOG_ENABLED) {
      console.log(`%c${LOG_PREFIX}`, 'color: #2196F3; font-weight: bold', new Date().toISOString(), ...args);
    }
  },
  success: (...args) => {
    if (LOG_ENABLED) {
      console.log(`%c${LOG_PREFIX}`, 'color: #4CAF50; font-weight: bold', new Date().toISOString(), ...args);
    }
  },
  warn: (...args) => {
    if (LOG_ENABLED) {
      console.warn(`%c${LOG_PREFIX}`, 'color: #FF9800; font-weight: bold', new Date().toISOString(), ...args);
    }
  },
  error: (...args) => {
    if (LOG_ENABLED) {
      console.error(`%c${LOG_PREFIX}`, 'color: #F44336; font-weight: bold', new Date().toISOString(), ...args);
    }
  }
};

/**
 * Verifica status do Service Worker
 */
function checkServiceWorkerStatus() {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.controller ? 'active' : 'not-active';
  }
  return 'not-supported';
}

/**
 * Verifica se está online
 */
function checkNetworkStatus() {
  return navigator.onLine ? 'online' : 'offline';
}

/**
 * Wrapper para lazy() com monitoramento completo
 */
export function monitorLazyLoad(importFn, componentName, retries = 3) {
  return React.lazy(() => {
    const startTime = performance.now();
    stats.totalLoads++;

    const swStatus = checkServiceWorkerStatus();
    const networkStatus = checkNetworkStatus();

    log.info(`🔄 Loading component: "${componentName}"`, {
      serviceWorker: swStatus,
      network: networkStatus,
      attempt: 1,
      totalLoadsToDate: stats.totalLoads
    });

    return new Promise((resolve, reject) => {
      let attempt = 0;

      const tryLoad = () => {
        attempt++;

        importFn()
          .then(module => {
            const loadTime = performance.now() - startTime;
            stats.successfulLoads++;
            stats.loadTimes.push(loadTime);

            log.success(`✅ Component loaded: "${componentName}"`, {
              loadTime: `${loadTime.toFixed(2)}ms`,
              attempt,
              serviceWorker: checkServiceWorkerStatus(),
              network: checkNetworkStatus(),
              successRate: `${((stats.successfulLoads / stats.totalLoads) * 100).toFixed(1)}%`
            });

            resolve(module);
          })
          .catch(error => {
            const isNetworkError =
              error?.message?.includes('Failed to fetch') ||
              error?.message?.includes('Loading chunk') ||
              error?.message?.includes('ChunkLoadError');

            if (attempt < retries && isNetworkError) {
              stats.retries++;
              const retryDelay = attempt * 500; // Backoff exponencial

              log.warn(`⚠️ Retry loading "${componentName}" (${attempt}/${retries})`, {
                error: error.message,
                retryDelay: `${retryDelay}ms`,
                serviceWorker: checkServiceWorkerStatus(),
                network: checkNetworkStatus()
              });

              setTimeout(tryLoad, retryDelay);
            } else {
              stats.failedLoads++;
              stats.failedChunks.push({
                component: componentName,
                error: error.message,
                timestamp: new Date().toISOString(),
                serviceWorker: checkServiceWorkerStatus(),
                network: checkNetworkStatus()
              });

              log.error(`❌ Failed to load "${componentName}" after ${attempt} attempts`, {
                error: error.message,
                serviceWorker: checkServiceWorkerStatus(),
                network: checkNetworkStatus(),
                failureRate: `${((stats.failedLoads / stats.totalLoads) * 100).toFixed(1)}%`,
                stackTrace: error.stack
              });

              reject(error);
            }
          });
      };

      tryLoad();
    });
  });
}

/**
 * Log de estatísticas consolidadas
 */
export function logLazyLoadStats() {
  if (!LOG_ENABLED) return;
  const avgLoadTime = stats.loadTimes.length > 0
    ? stats.loadTimes.reduce((a, b) => a + b, 0) / stats.loadTimes.length
    : 0;

  console.group(`${LOG_PREFIX} 📊 Lazy Load Statistics`);
  console.log('Total Loads:', stats.totalLoads);
  console.log('Successful:', stats.successfulLoads);
  console.log('Failed:', stats.failedLoads);
  console.log('Retries:', stats.retries);
  console.log('Success Rate:', `${((stats.successfulLoads / stats.totalLoads) * 100).toFixed(1)}%`);
  console.log('Average Load Time:', `${avgLoadTime.toFixed(2)}ms`);
  console.log('Service Worker:', checkServiceWorkerStatus());
  console.log('Network:', checkNetworkStatus());

  if (stats.failedChunks.length > 0) {
    console.group('Failed Chunks:');
    stats.failedChunks.forEach((fail, idx) => {
      console.log(`${idx + 1}.`, fail);
    });
    console.groupEnd();
  }

  console.groupEnd();
}

/**
 * Monitora eventos de network para detectar mudanças online/offline
 */
export function setupNetworkMonitoring() {
  window.addEventListener('online', () => {
    log.info('🌐 Network status: ONLINE');
  });

  window.addEventListener('offline', () => {
    log.warn('🔌 Network status: OFFLINE - Lazy chunks will load from SW cache');
  });

  // Log inicial
  log.info('🚀 Lazy Load Monitor initialized', {
    serviceWorker: checkServiceWorkerStatus(),
    network: checkNetworkStatus()
  });
}

/**
 * Exportar stats para debugging
 */
export function getLazyLoadStats() {
  return { ...stats };
}

/**
 * Reset stats (útil para testes)
 */
export function resetLazyLoadStats() {
  stats.totalLoads = 0;
  stats.successfulLoads = 0;
  stats.failedLoads = 0;
  stats.retries = 0;
  stats.loadTimes = [];
  stats.failedChunks = [];
  log.info('📊 Stats reset');
}

// Expor stats globalmente para debug
if (typeof window !== 'undefined') {
  window.__LAZY_LOAD_STATS__ = getLazyLoadStats;
  window.__LAZY_LOAD_STATS_LOG__ = logLazyLoadStats;
  window.__LAZY_LOAD_STATS_RESET__ = resetLazyLoadStats;
}

// Fazer React disponível (necessário para React.lazy)
import React from 'react';

export default {
  monitorLazyLoad,
  logLazyLoadStats,
  setupNetworkMonitoring,
  getLazyLoadStats,
  resetLazyLoadStats
};
